import { Checkbox, NumberInput } from '@/components'
import { LeaderGroupCombobox } from '@/components/combobox'
import { DropdownNames } from '@/components_deprecated/Select/types'
import { UserOrderBy } from '@/modules/activities'
import { useActivityExportMutations } from '@/modules/activities/mutations.hooks'
import { ALL_LEADER_GROUPS_QUERY } from '@/modules/companies'
import { ExportFormat, ExportOption, ExportType } from '@/modules/exports'
import {
  ExportCard,
  ExportField,
  ExportFormatField,
  ExportMethodField,
  ExportPeriodField,
  ExportUsersField,
  getExportPeriodIso,
} from '@/modules/exports/components/export-form'
import { SIMPLE_USERS_QUERY } from '@/modules/users/queries'
import { notEmptyArrayValidation } from '@/utils/forms'
import { getNodes } from '@/utils/graphql'
import { useCompany, useUser } from '@/utils/hooks'
import { isInvalidDate } from '@/utils/time'
import { useQuery } from '@apollo/client'
import { useForm, useTranslate } from '@ur-solutions/react-hooks'
import { useEffect, useMemo } from 'react'

interface ArbeidstilsynetExportForm {
  month: Date
  dateStart: Date
  dateEnd: Date
  exportOption: ExportOption
  exportFormat: ExportFormat
  useDateRange: boolean
  users: string[]
  leaderGroups: string[]
  otherWorkAsRest: boolean
  otherWorkAsRestHours: number | undefined
  excludeUnknownVehicleOnlyDays: boolean
}

const DEFAULT_OTHER_WORK_AS_REST_HOURS = 6

const DOWNLOAD_EXCLUDED_FORMATS = [
  ExportFormat.EXCEL,
  ExportFormat.EXCEL_FLAT,
  ExportFormat.PDF_SALARY,
  ExportFormat.POWEROFFICE,
  ExportFormat.TRIPLETEX,
  ExportFormat.TWENTYFOUR_SEVEN_OFFICE,
  ExportFormat.VISMA,
]

const EXCLUDED_FORMATS = [
  ExportFormat.EXCEL_FLAT,
  ExportFormat.PDF_SALARY,
  ExportFormat.POWEROFFICE,
  ExportFormat.TRIPLETEX,
  ExportFormat.TWENTYFOUR_SEVEN_OFFICE,
  ExportFormat.VISMA,
]

export const ExportArbeidstilsynetCard: React.FC = () => {
  const translations = useTranslate({
    title: 'exports.arbeidstilsynet.title',
    leaderGroups: 'companies.leader-groups',
    selectUsersError: 'activities.errors.select-multiple-users',
    downloadOnlySupportsPdf: 'settings.download-only-supports-pdf',
    otherWorkAsRest: 'exports.arbeidstilsynet.other-work-as-rest',
    otherWorkAsRestHours: 'exports.arbeidstilsynet.other-work-as-rest-hours',
    otherWorkAsRestHoursError:
      'exports.arbeidstilsynet.other-work-as-rest-hours-error',
    excludeUnknownVehicleOnlyDays:
      'exports.arbeidstilsynet.exclude-unknown-vehicle-days',
  })

  const me = useUser()
  const company = useCompany()
  const { exportActivitiesMass, loading } = useActivityExportMutations()
  const { data: companyUsersData } = useQuery(SIMPLE_USERS_QUERY, {
    variables: {
      first: 10000,
      q: '',
      orderBy: 'lastName',
      dropdown: DropdownNames.ACTIVITIES,
    },
    fetchPolicy: 'cache-and-network',
  })
  const { data: leaderGroupsData } = useQuery(ALL_LEADER_GROUPS_QUERY)

  const {
    formValues: form,
    formErrors: errors,
    submitHandler: submit,
    formValid,
    updateForm,
  } = useForm<ArbeidstilsynetExportForm>({
    values: {
      month: new Date(),
      dateStart: new Date(),
      dateEnd: new Date(),
      exportOption: ExportOption.SAVE,
      exportFormat: ExportFormat.PDF,
      useDateRange: false,
      users: [],
      leaderGroups: [],
      otherWorkAsRest: false,
      otherWorkAsRestHours: DEFAULT_OTHER_WORK_AS_REST_HOURS,
      excludeUnknownVehicleOnlyDays: false,
    },
    validators: {
      users: (value, other) =>
        notEmptyArrayValidation([...value, ...other.leaderGroups])
          ? translations.selectUsersError
          : null,
      leaderGroups: (value, other) =>
        notEmptyArrayValidation([...value, ...other.users])
          ? translations.selectUsersError
          : null,
      otherWorkAsRestHours: (value, other) =>
        other.otherWorkAsRest && !(value && value > 0)
          ? translations.otherWorkAsRestHoursError
          : null,
    },
    config: {
      initAsInvalid: true,
      storage: {
        storeFormState: true,
        retrieveFormStateOnMount: true,
        storeFormStateName: `Export-arbeidstilsynet-form-${company.id}`,
        transformValues: {
          month: value =>
            typeof value === 'string'
              ? isInvalidDate(new Date(value), new Date())
              : undefined,
          dateStart: value =>
            typeof value === 'string'
              ? isInvalidDate(new Date(value), new Date())
              : undefined,
          dateEnd: value =>
            typeof value === 'string'
              ? isInvalidDate(new Date(value), new Date())
              : undefined,
          exportFormat: value => value as ExportFormat,
          exportOption: value => value as ExportOption,
          useDateRange: value => value as boolean,
          users: value =>
            Array.isArray(value)
              ? value.filter(
                  (userId): userId is string => typeof userId === 'string'
                )
              : [],
          leaderGroups: value =>
            Array.isArray(value)
              ? value.filter(
                  (groupId): groupId is string => typeof groupId === 'string'
                )
              : [],
          otherWorkAsRest: value => value === true,
          otherWorkAsRestHours: value =>
            typeof value === 'number'
              ? value
              : DEFAULT_OTHER_WORK_AS_REST_HOURS,
          excludeUnknownVehicleOnlyDays: value => value === true,
        },
      },
    },
  })

  const companyUserIds = useMemo(
    () => new Set(getNodes(companyUsersData?.allUsers).map(user => user.id)),
    [companyUsersData?.allUsers]
  )

  useEffect(() => {
    if (companyUserIds.size === 0 || form.users.length === 0) return

    const companyUsers = form.users.filter(userId => companyUserIds.has(userId))
    if (companyUsers.length === form.users.length) return

    updateForm({ users: companyUsers })
  }, [companyUserIds, form.users, updateForm])

  const leaderGroupIds = useMemo(
    () =>
      new Set((leaderGroupsData?.allLeaderGroups ?? []).map(group => group.id)),
    [leaderGroupsData?.allLeaderGroups]
  )

  useEffect(() => {
    if (!leaderGroupsData || form.leaderGroups.length === 0) return

    const companyLeaderGroups = form.leaderGroups.filter(groupId =>
      leaderGroupIds.has(groupId)
    )
    if (companyLeaderGroups.length === form.leaderGroups.length) return

    updateForm({ leaderGroups: companyLeaderGroups })
  }, [leaderGroupIds, leaderGroupsData, form.leaderGroups, updateForm])

  function handleExport(values: ArbeidstilsynetExportForm) {
    const [dateStart, dateEnd] = getExportPeriodIso(values)

    void exportActivitiesMass({
      variables: {
        exportType: ExportType.MASS_ARBEIDSTILSYNET,
        exportFormat: values.exportFormat,
        user: me.id,
        users: values.users,
        company: company.id,
        dateStart,
        dateEnd,
        orderBy: UserOrderBy.FAMILY_NAME,
        includeAbsences: false,
        exportOption: values.exportOption,
        weekly: false,
        leaderGroups: values.leaderGroups,
        otherWorkAsRestHours: values.otherWorkAsRest
          ? values.otherWorkAsRestHours
          : null,
        excludeUnknownVehicleOnlyDays: values.excludeUnknownVehicleOnlyDays,
      },
    })
  }

  const downloadOnly = form.exportOption === ExportOption.DOWNLOAD

  return (
    <ExportCard
      title={translations.title}
      loading={loading}
      submitDisabled={!formValid}
      onSubmit={submit(handleExport)}
    >
      <ExportPeriodField value={form} onChange={updateForm} />

      <ExportMethodField
        value={form.exportOption}
        allowDownload
        onChange={exportOption => updateForm({ exportOption })}
      />

      <ExportFormatField
        value={form.exportFormat}
        exclude={downloadOnly ? DOWNLOAD_EXCLUDED_FORMATS : EXCLUDED_FORMATS}
        disabled={downloadOnly}
        hint={downloadOnly ? translations.downloadOnlySupportsPdf : null}
        onChange={exportFormat => updateForm({ exportFormat })}
      />

      <ExportField label={translations.leaderGroups}>
        <LeaderGroupCombobox
          mode="multiple"
          value={form.leaderGroups}
          onChange={leaderGroups => updateForm({ leaderGroups })}
        />
      </ExportField>

      <ExportUsersField
        value={form.users}
        error={errors.users}
        dropdownPageName={DropdownNames.ACTIVITIES}
        onChange={users => updateForm({ users })}
      />

      <label className="flex cursor-pointer items-center gap-2 text-sm text-stone-700 md:col-span-2">
        <Checkbox
          size="md"
          value={form.otherWorkAsRest}
          onChange={checked =>
            updateForm({ otherWorkAsRest: checked === true })
          }
        />
        {translations.otherWorkAsRest}
      </label>

      {form.otherWorkAsRest && (
        <ExportField
          label={translations.otherWorkAsRestHours}
          error={errors.otherWorkAsRestHours}
        >
          <NumberInput
            value={form.otherWorkAsRestHours}
            min={0}
            decimalScale={2}
            onChange={otherWorkAsRestHours =>
              updateForm({ otherWorkAsRestHours })
            }
          />
        </ExportField>
      )}

      <label className="flex cursor-pointer items-center gap-2 text-sm text-stone-700 md:col-span-2">
        <Checkbox
          size="md"
          value={form.excludeUnknownVehicleOnlyDays}
          onChange={checked =>
            updateForm({ excludeUnknownVehicleOnlyDays: checked === true })
          }
        />
        {translations.excludeUnknownVehicleOnlyDays}
      </label>
    </ExportCard>
  )
}
